/**
 * What a request header carries: the rule a credential's members and a client's `language` are
 * held to where the client is built, since each of them is sent as a header's value.
 *
 * A value outside it never reaches the API. The runtime's `fetch` refuses the request, and with
 * no rule here the refusal surfaces from the first call as a connection failure, retried and
 * reported with a next step about the network.
 *
 * It is HTTP's rule and nobody's format: it says nothing of what a key or a language tag looks
 * like, so it refuses nothing the API would have accepted.
 */

/** Matches the whitespace a runtime's `fetch` takes off either end of a header's value. */
const edgeWhitespacePattern = /^[\t\n\r ]+|[\t\n\r ]+$/g;

/**
 * Matches what an HTTP field value holds between its ends: a tab, a space, visible ASCII, and a
 * byte from 0x80 to 0xFF, which is how a character of Latin-1 travels.
 */
const fieldValuePattern = /^[\t\x20-\x7e\x80-\xff]*$/;

/**
 * isHeaderValue reports whether a request can carry `value` as a header's value.
 *
 * Whitespace at either end is no part of the question: a runtime sends the value without it, so
 * a key read from a file with its closing line break is carried. What no request carries is a
 * line break, a NUL or another control character inside the value, and a character past Latin-1,
 * the ellipsis of a placeholder among them.
 */
export const isHeaderValue = (value: string): boolean =>
  fieldValuePattern.test(value.replace(edgeWhitespacePattern, ""));

/**
 * isEmptyHeaderValue reports whether a request would carry `value` as a header with nothing in
 * it: an empty string, or whitespace alone, which a runtime takes off before it sends the value.
 *
 * It is the question for a value that has to name something: a client's `language`, a call's
 * `idempotencyKey` and its `ifMatch`. One of a single space is sent as an empty
 * `Accept-Language`, `Idempotency-Key` or `If-Match`, exactly as an empty one is.
 */
export const isEmptyHeaderValue = (value: string): boolean =>
  value.replace(edgeWhitespacePattern, "") === "";
